import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { TokenUsage } from '@omni/ai/transports/ai-transport';

/** Payload for a rep's vote — `comment` is only ever present on a `down` vote. */
export interface NovaFeedbackEvent {
  responseId: string;
  up: boolean;
  comment?: string;
}

/** `didDismiss` carries the role the dismisser passed to `.dismiss()` — Ionic supplies its own
 *  (`'backdrop'`, `'escape'`, `'gesture'`) for the dismiss paths we don't wire up ourselves. */
type PopoverDismissEvent = CustomEvent<{ role?: string }>;

/**
 * Per-response feedback strip: a thumbs up/down vote plus a token-usage chip. Shared by every
 * Nova AI card surface (assistant chat, AI Summary, Nova Insights) that can name the turn it
 * rendered — `<nova-card>` embeds it for the two card-based surfaces, and the AI Summary brief
 * (which renders its own markup, not `<nova-card>`) embeds it directly. Purely presentational:
 * the host owns submitting the vote via `AssistantFeedbackService`.
 *
 * The thumbs-down reason is captured in an `ion-popover` (floats above content, anchored to the
 * 👎 button) rather than inline markup — a popover can never reflow the card it's rendered in.
 */
@Component({
  selector: 'nova-feedback-footer',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './nova-feedback-footer.component.html',
  styleUrls: ['./nova-feedback-footer.component.scss'],
})
export class NovaFeedbackFooterComponent implements OnChanges {
  /** Responses API id of the turn this footer rates. The footer renders nothing without one —
   *  there is nothing to correlate a vote against. */
  @Input() responseId?: string | null = null;
  @Input() usage?: TokenUsage | null = null;

  @Output() feedback = new EventEmitter<NovaFeedbackEvent>();

  /** Only used to force-close a stray popover if the host hands us a new `responseId` (e.g. a
   *  regenerate) while the rep still has the reason popover open on the old one. */
  @ViewChild('downPopover')
  private readonly downPopover?: HTMLIonPopoverElement;

  /** Which vote the rep already cast for this response, if any — fills in the matching icon. */
  voted: boolean | null = null;
  /** True once a vote has actually been submitted (👍 immediately, 👎 on confirm — never on
   *  cancel) — swaps the buttons for a thank-you so a response can't be re-rated. */
  submitted = false;
  /** Draft text for the open reason popover. */
  comment = '';

  readonly #translate = inject(TranslateService);

  /** "1,240 tokens", in the app language. */
  get tokenLabel(): string | null {
    const total = this.usage?.totalTokens;
    return total == null
      ? null
      : this.#translate.instant('AI_TOKEN_COUNT', {
          count: total.toLocaleString(),
        });
  }

  /** "980 in · 260 out" — the input/output split, shown as a tooltip on the token chip, in the app language. */
  get tokenTitle(): string | null {
    if (!this.usage) return null;
    return this.#translate.instant('AI_TOKEN_SPLIT', {
      input: this.usage.inputTokens.toLocaleString(),
      output: this.usage.outputTokens.toLocaleString(),
    });
  }

  /** A host that reuses this instance across regenerations (record-summary, AI Summary) hands us
   *  a new `responseId` rather than recreating the component — clear the stale vote, and force-
   *  close the reason popover if it happened to be open on the response being replaced. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.responseId && !changes.responseId.firstChange) {
      this.voted = null;
      this.submitted = false;
      this.comment = '';
      void this.downPopover?.dismiss();
    }
  }

  /**
   * 👍 — votes and emits immediately, one click, as before.
   * 👎 — marks down and lets the popover (opened by the template's `.present()` call) capture the
   * reason; nothing emits until the popover reports how it closed via {@link onPopoverDismiss}.
   */
  vote(up: boolean): void {
    if (!this.responseId) return;
    this.voted = up;
    if (up) {
      this.feedback.emit({ responseId: this.responseId, up: true });
      this.submitted = true;
    }
  }

  /**
   * The reason popover closed — decide the outcome from the role it was dismissed with:
   *  - `'confirm'` (✓ / Enter): emit the down vote with whatever reason was typed (empty is fine).
   *  - anything else (✗ / Escape / backdrop): a genuine cancel — reset `voted` to null, emit nothing.
   *
   * Guarded against the switch-to-👍 race: clicking 👍 while the popover is open calls
   * `downPopover.dismiss()` and `vote(true)` back-to-back — `vote` runs synchronously and sets
   * `voted = true` well before this handler fires (dismiss is async, after the close animation).
   * So `voted === false` here means nothing has since overridden the down vote, i.e. it really was
   * a cancel; if `voted` is already `true`, this is that race and must NOT be reset to null.
   */
  onPopoverDismiss(event: PopoverDismissEvent): void {
    if (event.detail.role === 'confirm') {
      if (this.responseId) {
        this.feedback.emit({
          responseId: this.responseId,
          up: false,
          comment: this.comment.trim(),
        });
        this.submitted = true;
      }
    } else if (this.voted === false) {
      this.voted = null;
    }
    this.comment = '';
  }
}
